import { OBSERVABILITY_POLL_MS, useFleetObservability } from "../observability-queries";
export { OBSERVABILITY_POLL_MS, sumMetrics, useFleetObservability } from "../observability-queries";
import { FleetSummary } from "./FleetSummary";
import { DeploymentStatusBadge } from "./DeploymentStatusBadge";
import { lifecycleState } from "../lifecycle";
import { fieldControlClass } from "@/components/ui/field-styles";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ArrowDownToLine,
  ArrowUpRight,
  Gauge,
  Hourglass,
  Pause,
  Play,
  Radio,
  Timer,
  Users,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/utils/cn";
import { omnimizerApi, omnimizerKeys } from "../api";
import { useOmnimizerDeployments } from "../catalog-sync";
import { deploymentState, metricValue } from "../state";
import type {
  ObservabilityResponse,
  OmnimizerDeployment,
  OwnerAnalytics,
  WorkerMetrics,
} from "../types";

// Prometheus scrapes workers every 30s; polling faster only repeats samples.

const STALE_AFTER_MS = 90000;
const SPARK_POINTS = 40;

const RANGES = [1, 24, 168, 720] as const;
const OWNER_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];
const OTHER_COLOR = "var(--muted-foreground)";

type MetricKey = keyof WorkerMetrics;
const METRICS: { key: MetricKey; icon: LucideIcon; unit: "tokens" | "requestRate" | "requests" }[] = [
  { key: "output_tokens_per_second", icon: Zap, unit: "tokens" },
  { key: "input_tokens_per_second", icon: ArrowDownToLine, unit: "tokens" },
  { key: "requests_per_second", icon: Activity, unit: "requestRate" },
  { key: "running", icon: Radio, unit: "requests" },
  { key: "queued", icon: Hourglass, unit: "requests" },
];

export type Signal = "live" | "stale" | "paused" | "off" | "loading";

export function signalFor(
  data: ObservabilityResponse | undefined,
  deployment: OmnimizerDeployment,
  updatedAt: number,
  now: number,
): Signal {
  if (!data) return "loading";
  if (deploymentState(deployment) === "paused") return "paused";
  if (!data.configured || !data.available) return "off";
  return now - updatedAt > STALE_AFTER_MS ? "stale" : "live";
}

function useNow(intervalMs = 5000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function SignalChip({ signal, updatedAt, now }: { signal: Signal; updatedAt: number; now: number }) {
  const { t } = useTranslation();
  const seconds = Math.max(0, Math.round((now - updatedAt) / 1000));
  const label = {
    live: t("omnimizer.observability.signalLive", { seconds }),
    stale: t("omnimizer.observability.signalStale", { seconds }),
    paused: t("omnimizer.observability.signalPaused"),
    off: t("omnimizer.observability.signalOff"),
    loading: t("omnimizer.observability.signalLoading"),
  }[signal];
  return (
    <span
      role="status"
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold",
        signal === "live" && "bg-primary/15 text-foreground",
        (signal === "stale" || signal === "paused") && "bg-[var(--warning-100)] text-foreground",
        (signal === "off" || signal === "loading") && "bg-muted text-muted-foreground",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "size-2 rounded-full bg-current",
          signal === "live" && "animate-pulse bg-primary motion-reduce:animate-none",
          (signal === "stale" || signal === "paused") && "bg-[var(--warning)]",
        )}
      />
      {label}
    </span>
  );
}

function Sparkline({ points, label }: { points: number[]; label: string }) {
  const { t } = useTranslation();
  const width = 160;
  const height = 44;
  if (points.length < 2)
    return (
      <svg className="block h-11 w-full" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={t("omnimizer.observability.collecting", { label })}>
        <line x1="0" x2={width} y1={height - 2} y2={height - 2} className="stroke-border" />
      </svg>
    );
  const max = Math.max(...points, 1e-9);
  const step = width / (points.length - 1);
  const xy = points.map((p, i) => [i * step, height - 4 - (p / max) * (height - 10)] as const);
  const line = xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const last = xy[xy.length - 1];
  return (
    <svg
      className="block h-11 w-full"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={t("omnimizer.observability.sparkline", {
        label,
        count: points.length,
        latest: metricValue(points[points.length - 1]),
        peak: metricValue(max),
      })}
    >
      <line x1="0" x2={width} y1={height - 2} y2={height - 2} className="stroke-border" />
      <path d={`${line} L${last[0]},${height - 2} L${xy[0][0]},${height - 2} Z`} className="fill-primary/15" />
      <path d={line} className="fill-none stroke-primary" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r={3} className="fill-primary" />
    </svg>
  );
}

function QueueGauge({ metrics, available }: { metrics: WorkerMetrics; available: boolean }) {
  const { t } = useTranslation();
  const running = metrics.running ?? null;
  const queued = metrics.queued ?? null;
  const known = available && running != null && queued != null;
  const total = (running ?? 0) + (queued ?? 0);
  const pressure = known && total > 0 ? (queued ?? 0) / total : 0;
  const arc = Math.PI * 80;
  const tone =
    pressure >= 0.5 ? "stroke-destructive" : pressure >= 0.2 ? "stroke-[var(--warning)]" : "stroke-primary";
  const caption = !known
    ? t("omnimizer.observability.gaugeNoData")
    : total === 0
      ? t("omnimizer.observability.gaugeIdle")
      : pressure >= 0.5
        ? t("omnimizer.observability.gaugeSaturated")
        : pressure >= 0.2
          ? t("omnimizer.observability.gaugeBuilding")
          : t("omnimizer.observability.gaugeHealthy");
  return (
    <Card className="flex flex-col gap-3 p-5">
      <h2 className="flex items-center gap-2 text-base font-semibold">
        <Gauge className="size-4 text-primary" aria-hidden />
        {t("omnimizer.observability.queuePressure")}
      </h2>
      <div className="flex flex-col items-center gap-2">
        <svg viewBox="0 0 200 116" className="h-28 w-52" aria-hidden>
          <path d="M20,100 A80,80 0 0 1 180,100" fill="none" strokeWidth={14} strokeLinecap="round" className="stroke-muted" />
          {known && (
            <path
              d="M20,100 A80,80 0 0 1 180,100"
              fill="none"
              strokeWidth={14}
              strokeLinecap="round"
              strokeDasharray={arc}
              strokeDashoffset={arc * (1 - pressure)}
              className={cn(tone, "transition-[stroke-dashoffset] duration-500 motion-reduce:transition-none")}
            />
          )}
        </svg>
        <div className="-mt-16 text-3xl font-semibold tabular-nums">
          {known ? `${Math.round(pressure * 100)}%` : "—"}
        </div>
        <p className="max-w-64 text-center text-sm text-muted-foreground">{caption}</p>
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>
            {t("omnimizer.observability.running")}{" "}
            <b className="text-foreground tabular-nums">{known ? metricValue(running, 0) : "—"}</b>
          </span>
          <span>
            {t("omnimizer.observability.queued")}{" "}
            <b className="text-foreground tabular-nums">{known ? metricValue(queued, 0) : "—"}</b>
          </span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t("omnimizer.observability.gaugeFormula")}</p>
    </Card>
  );
}

function TokenShare({ users }: { users: OwnerAnalytics[] }) {
  const { t } = useTranslation();
  const sorted = [...users].sort((a, b) => (b.completion_tokens ?? 0) - (a.completion_tokens ?? 0));
  const total = sorted.reduce((sum, u) => sum + (u.completion_tokens ?? 0), 0);
  const top = sorted.slice(0, OWNER_COLORS.length);
  const rest = sorted.slice(OWNER_COLORS.length).reduce((sum, u) => sum + (u.completion_tokens ?? 0), 0);
  const requests = sorted.reduce((sum, u) => sum + u.requests, 0);
  const failures = sorted.reduce((sum, u) => sum + u.failures, 0);
  const pct = (v: number) => `${(v / total) * 100}%`;
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Users className="size-4 text-primary" aria-hidden />
          {t("omnimizer.observability.tokenShare")}
        </h2>
        <span className="text-sm text-muted-foreground tabular-nums">
          {t("omnimizer.observability.requestsSummary", {
            requests: metricValue(requests, 0),
            failures: metricValue(failures, 0),
          })}
        </span>
      </div>
      {total > 0 ? (
        <>
          <div className="flex h-3.5 overflow-hidden rounded-full bg-muted" role="img" aria-label={t("omnimizer.observability.tokenShare")}>
            {top.map((u, i) => (
              <span key={u.user_id} className="h-full border-e-2 border-card" style={{ width: pct(u.completion_tokens ?? 0), background: OWNER_COLORS[i] }} />
            ))}
            {rest > 0 && <span className="h-full" style={{ width: pct(rest), background: OTHER_COLOR }} />}
          </div>
          <ol className="flex flex-col gap-2.5">
            {top.map((u, i) => (
              <li key={u.user_id} className="grid grid-cols-[10px_80px_minmax(40px,1fr)_auto] items-center gap-3 text-sm sm:grid-cols-[10px_80px_minmax(60px,1fr)_100px_70px_90px]">
                <i className="size-2.5 rounded-sm" style={{ background: OWNER_COLORS[i] }} aria-hidden />
                <code className="text-xs" title={u.user_id}>{u.user_id.slice(0, 8)}</code>
                <span className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <span className="block h-full rounded-full" style={{ width: `${((u.completion_tokens ?? 0) / (top[0].completion_tokens || 1)) * 100}%`, background: OWNER_COLORS[i] }} />
                </span>
                <b className="text-end tabular-nums">
                  {t("omnimizer.observability.tokensShort", { value: metricValue(u.completion_tokens, 0) })}
                </b>
                <span className="hidden text-end text-muted-foreground tabular-nums sm:block">
                  {t("omnimizer.observability.requestsShort", { value: metricValue(u.requests, 0) })}
                </span>
                <span className="hidden text-end text-muted-foreground tabular-nums sm:block">
                  TTFT {u.mean_ttft_seconds == null ? "—" : `${metricValue(u.mean_ttft_seconds, 2)} s`}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-muted-foreground">
            {t("omnimizer.observability.knownTokens", { value: metricValue(total, 0) })}
          </p>
        </>
      ) : (
        <p className="py-4 text-sm text-muted-foreground">{t("omnimizer.observability.noUsage")}</p>
      )}
    </Card>
  );
}

function OwnersTable({ users }: { users: OwnerAnalytics[] }) {
  const { t } = useTranslation();
  const sorted = [...users].sort((a, b) => b.requests - a.requests);
  const columns = [
    "owner",
    "requests",
    "tokens",
    "generationRate",
    "requestRate",
    "throughput",
    "ttft",
    "outcomes",
  ] as const;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px] border-collapse text-start text-sm">
        <thead>
          <tr className="border-y border-border bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            {columns.map((c) => (
              <th key={c} scope="col" className="px-4 py-3 text-start font-semibold">
                {t(`omnimizer.observability.columns.${c}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((u) => (
            <tr key={u.user_id} className="border-b border-border tabular-nums hover:bg-muted/40">
              <td className="px-4 py-3"><code className="text-xs" title={u.user_id}>{u.user_id.slice(0, 12)}</code></td>
              <td className="px-4 py-3">{metricValue(u.requests, 0)} · {metricValue(u.requests_with_usage, 0)}</td>
              <td className="px-4 py-3">{metricValue(u.prompt_tokens, 0)} / {metricValue(u.completion_tokens, 0)}</td>
              <td className="px-4 py-3">{metricValue(u.observed_generation_tokens_per_second)}</td>
              <td className="px-4 py-3">{metricValue(u.average_request_tokens_per_second)}</td>
              <td className="px-4 py-3">{metricValue(u.user_throughput_tokens_per_second, 2)}</td>
              <td className="px-4 py-3">{u.mean_ttft_seconds == null ? "—" : `${metricValue(u.mean_ttft_seconds, 2)} s`}</td>
              <td className="px-4 py-3">
                <span className="flex gap-1.5">
                  <Badge variant={u.failures ? "destructive" : "muted"}>
                    {t("omnimizer.observability.failures", { count: u.failures })}
                  </Badge>
                  <Badge variant="muted">{t("omnimizer.observability.cancellations", { count: u.cancellations })}</Badge>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Session-local sample history, kept per deployment across navigation.
const sampleHistory = new Map<string, { at: number; series: Record<string, number[]> }>();

export function ObservabilityPanel({
  deployment,
  hours,
  paused,
}: {
  deployment: OmnimizerDeployment;
  hours: number;
  paused: boolean;
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: omnimizerKeys.observability(deployment.model_id, deployment.version, hours),
    queryFn: () => omnimizerApi.observability(deployment, hours),
    refetchInterval: paused ? false : OBSERVABILITY_POLL_MS,
  });
  const data = query.data;
  const historyKey = `${deployment.model_id}:${deployment.version}`;
  const [history, setHistory] = useState<Record<string, number[]>>(
    () => sampleHistory.get(historyKey)?.series ?? {},
  );
  useEffect(() => {
    const stored = sampleHistory.get(historyKey);
    if (!data?.available || !query.dataUpdatedAt || stored?.at === query.dataUpdatedAt) return;
    const next: Record<string, number[]> = {};
    for (const { key } of METRICS) {
      const value = data.metrics[key];
      const series = stored?.series[key] ?? [];
      next[key] = value == null ? series : [...series, value].slice(-SPARK_POINTS);
    }
    sampleHistory.set(historyKey, { at: query.dataUpdatedAt, series: next });
    setHistory(next);
    // One point per fetch: structural sharing keeps `data` stable when values repeat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.dataUpdatedAt]);
  const metrics = data?.available ? data.metrics : {};
  const users = data?.analytics.users ?? [];
  return (
    <div className="flex flex-col gap-4">
      {query.error && (
        <p role="alert" className="rounded-lg border border-destructive bg-[var(--destructive-100)] p-3 text-sm text-foreground">
          {query.error instanceof Error ? query.error.message : t("omnimizer.observability.loadFailed")}
        </p>
      )}
      {data && (!data.configured || !data.available) && (
        <p className="rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm text-foreground">
          {!data.configured
            ? t("omnimizer.observability.notConfigured")
            : data.message ||
              (deploymentState(deployment) === "paused"
                ? t("omnimizer.observability.pausedNotice")
                : t("omnimizer.observability.noSamples"))}
        </p>
      )}
      {data?.grafana_url && (
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={data.grafana_url} target="_blank" rel="noreferrer">
              {t("omnimizer.observability.grafanaModels")} <ArrowUpRight aria-hidden />
            </a>
          </Button>
          {data.users_grafana_url && (
            <Button asChild variant="outline" size="sm">
              <a href={data.users_grafana_url} target="_blank" rel="noreferrer">
                {t("omnimizer.observability.grafanaRequests")} <ArrowUpRight aria-hidden />
              </a>
            </Button>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {METRICS.map(({ key, icon: Icon, unit }) => {
          const value = metrics[key];
          const label = t(`omnimizer.observability.metrics.${key}`);
          return (
            <Card key={key} className="flex flex-col gap-2 p-4">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                {label}
                <Icon className="size-4 text-primary" aria-hidden />
              </div>
              <div className="flex items-baseline gap-1.5">
                <strong className={cn("font-semibold tabular-nums", value == null ? "text-lg text-muted-foreground" : "text-2xl")}>
                  {value == null ? t("omnimizer.common.noData") : metricValue(value, unit === "requests" ? 0 : 1)}
                </strong>
                {value != null && <span className="text-xs text-muted-foreground">{t(`omnimizer.observability.units.${unit}`)}</span>}
              </div>
              <Sparkline points={history[key] ?? []} label={label} />
              <p className="text-xs text-muted-foreground">{t(`omnimizer.observability.help.${key}`)}</p>
            </Card>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">{t("omnimizer.observability.sparklineNote")}</p>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <QueueGauge metrics={metrics} available={!!data?.available} />
        <TokenShare users={users} />
      </div>
      <Card className="gap-0 overflow-hidden p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 p-4">
          <h2 className="text-base font-semibold">{t("omnimizer.observability.ownersTitle")}</h2>
          <span className="text-sm text-muted-foreground">{t("omnimizer.observability.ownersSubtitle")}</span>
        </div>
        {data?.analytics.message && (
          <p className="mx-4 mb-3 rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm text-foreground">{data.analytics.message}</p>
        )}
        {users.length ? (
          <OwnersTable users={users} />
        ) : (
          <div className="flex flex-col items-center gap-3 px-4 py-10 text-center text-sm text-muted-foreground">
            <Timer className="size-7 text-primary" aria-hidden />
            {query.isPending ? t("omnimizer.observability.loadingAnalytics") : t("omnimizer.observability.noAnalytics")}
          </div>
        )}
        <p className="border-t border-border p-4 text-xs text-muted-foreground">{t("omnimizer.observability.ratesNote")}</p>
      </Card>
    </div>
  );
}

export function ObservabilityPage({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (deploymentId: string) => void;
}) {
  const { t } = useTranslation();
  const [hours, setHours] = useState<number>(1);
  const [paused, setPaused] = useState(false);
  const now = useNow();
  const deployments = useOmnimizerDeployments(true, paused ? false : 15000);
  const items = useMemo(() => (deployments.data || []).filter((d) => d.is_active), [deployments.data]);
  const fleet = useFleetObservability(items, paused);
  const active =
    items.find((d) => d.model_id === selectedId) ||
    items.find((d) => deploymentState(d) === "ready") ||
    items[0];
  const activeQuery = fleet.find((f) => f.deployment === active)?.query;

  if (deployments.error)
    return (
      <p role="alert" className="rounded-lg border border-destructive bg-[var(--destructive-100)] p-3 text-sm text-foreground">
        {deployments.error instanceof Error ? deployments.error.message : t("omnimizer.common.loadFailed")}
      </p>
    );
  if (!active)
    return (<div className="flex flex-col gap-4"><FleetSummary items={items} fleet={fleet} />
      <Card className="flex flex-col items-center gap-3 p-10 text-center">
        <Activity className="size-8 text-primary" aria-hidden />
        <h2 className="text-lg font-semibold">
          {deployments.isPending ? t("omnimizer.common.loading") : t("omnimizer.observability.emptyTitle")}
        </h2>
        <p className="text-sm text-muted-foreground">{t("omnimizer.observability.emptyBody")}</p>
      </Card></div>
    );

  return (
    <div className="flex flex-col gap-4">
      <FleetSummary items={items} fleet={fleet} />
      <Card className="flex flex-row flex-wrap items-center gap-3 p-3">
        <label className="sr-only" htmlFor="observe-deployment">{t("omnimizer.observability.deploymentLabel")}</label>
        <select
          id="observe-deployment"
          className={`${fieldControlClass} h-10 basis-full rounded-full px-4 sm:max-w-80 sm:flex-1 sm:basis-auto`}
          value={active.model_id}
          onChange={(e) => onSelect(e.target.value)}
        >
          {items.map((d) => (
            <option key={d.model_id} value={d.model_id}>
              {d.name} · v{d.version} · {t(`omnimizer.lifecycle.${lifecycleState(d)}`)}
            </option>
          ))}
        </select>
        <div className="flex gap-1" role="group" aria-label={t("omnimizer.observability.rangeLabel")}>
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={hours === r}
              onClick={() => setHours(r)}
              className={cn(
                "min-h-9 rounded-full border px-3.5 text-xs font-medium transition-colors",
                hours === r ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              {t(`omnimizer.observability.ranges.${r}`)}
            </button>
          ))}
        </div>
        <span className="flex-1" />
        <DeploymentStatusBadge deployment={active} />
        <SignalChip
          signal={paused ? "paused" : activeQuery ? signalFor(activeQuery.data, active, activeQuery.dataUpdatedAt, now) : deploymentState(active) === "paused" ? "paused" : "off"}
          updatedAt={activeQuery?.dataUpdatedAt ?? 0}
          now={now}
        />
        <Button variant="outline" size="sm" onClick={() => setPaused(!paused)} aria-pressed={paused}>
          {paused ? <Play aria-hidden /> : <Pause aria-hidden />}
          {paused ? t("omnimizer.observability.resume") : t("omnimizer.observability.pause")}
        </Button>
      </Card>
      {fleet.length > 1 && (
        <Card className="flex flex-col gap-3 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <Radio className="size-4 text-primary" aria-hidden />
              {t("omnimizer.observability.fleetTitle")}
            </h2>
            <span className="text-sm text-muted-foreground">{t("omnimizer.observability.fleetSubtitle")}</span>
          </div>
          <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
            {fleet.map(({ deployment: d, query }) => {
              const m = query.data?.available ? query.data.metrics : null;
              const pressed = d === active;
              return (
                <button
                  key={d.model_id}
                  type="button"
                  aria-pressed={pressed}
                  onClick={() => onSelect(d.model_id)}
                  className={cn(
                    "flex flex-col gap-2 rounded-xl border bg-muted/30 p-3 text-start transition-colors hover:border-ring",
                    pressed ? "border-primary ring-2 ring-primary/30" : "border-border",
                  )}
                >
                  <span className="flex items-center justify-between gap-2 text-sm font-semibold">
                    <span className="truncate">{d.name}</span>
                    <Badge variant={m ? "success" : "muted"}>{m ? t("omnimizer.observability.live") : t("omnimizer.common.noData")}</Badge>
                  </span>
                  <dl className="grid grid-cols-3 gap-1.5">
                    {(["output_tokens_per_second", "running", "queued"] as const).map((k) => (
                      <div key={k}>
                        <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{t(`omnimizer.observability.fleetMetrics.${k}`)}</dt>
                        <dd className="text-base font-semibold tabular-nums">{metricValue(m?.[k], k === "output_tokens_per_second" ? 1 : 0)}</dd>
                      </div>
                    ))}
                  </dl>
                </button>
              );
            })}
          </div>
        </Card>
      )}
      <ObservabilityPanel key={`${active.model_id}:${active.version}`} deployment={active} hours={hours} paused={paused} />
    </div>
  );
}
